import { USER_TYPES } from '@/constants/constants';

/**
 * Whether the verify-otp/magic-link `role` is the Clinic Owner role.
 * @param {{ name: string } | string | undefined} role
 * @returns {boolean}
 */
export const isClinicOwnerRole = role => {
  const name = typeof role === 'string' ? role : role?.name;
  return typeof name === 'string' && name.trim().toLowerCase() === 'clinic owner';
};

/**
 * Whether the verify-otp/magic-link `role` is the Solo Therapist role (an
 * independently signed-up therapist running their own practice).
 * @param {{ name: string } | string | undefined} role
 * @returns {boolean}
 */
export const isSoloTherapistRole = role => {
  const name = typeof role === 'string' ? role : role?.name;
  return typeof name === 'string' && name.trim().toLowerCase() === 'solo therapist';
};

/**
 * Whether the verify-otp/magic-link `role` is a Therapist role - a clinic-invited
 * Therapist or a Solo Therapist; both are treated as the same app persona.
 * @param {{ name: string } | string | undefined} role
 * @returns {boolean}
 */
export const isTherapistRole = role => {
  const name = typeof role === 'string' ? role : role?.name;
  return (
    (typeof name === 'string' && name.trim().toLowerCase() === 'therapist') ||
    isSoloTherapistRole(name)
  );
};

/**
 * Map API role name from /auth/verify-otp response to app user type
 * @param {{ id: number, name: string } | undefined} role - role from verify-otp response
 * @returns {'patient' | 'therapist' | 'dashboard' | 'clinicOwner'}
 */
export const getUserTypeFromRole = role => {
  if (!role?.name) {
    return USER_TYPES.THERAPIST;
  }
  if (isClinicOwnerRole(role)) {
    return USER_TYPES.CLINIC_OWNER;
  }
  const name = String(role.name).toLowerCase();
  if (name === 'patient') {
    return USER_TYPES.PATIENT;
  }
  if (name === 'admin') {
    return USER_TYPES.DASHBOARD;
  }
  if (isTherapistRole(name)) {
    return USER_TYPES.THERAPIST;
  }
  if (name === 'medical director') {
    return USER_TYPES.MEDICAL_DIRECTOR;
  }
  if (name === 'receptionist') {
    return USER_TYPES.RECEPTIONIST;
  }
  return USER_TYPES.THERAPIST;
};

/**
 * Utility function to detect user type based on current route
 * @param {string} pathname - The current route pathname
 * @returns {'patient' | 'therapist' | 'dashboard'} - The detected user type
 */
export const detectUserTypeFromRoute = pathname => {
  // Dashboard routes patterns
  const dashboardRoutePatterns = ['/login'];
  if (dashboardRoutePatterns.some(pattern => pathname.includes(pattern))) {
    return USER_TYPES.DASHBOARD;
  }

  // Patient routes patterns
  const patientRoutePatterns = ['/login'];
  if (patientRoutePatterns.some(pattern => pathname.includes(pattern))) {
    return USER_TYPES.PATIENT;
  }

  return USER_TYPES.THERAPIST;
};

/**
 * Get the localStorage key value for user type
 * @param {'patient' | 'therapist' | 'dashboard'} userType - The user type
 * @returns {string} - The value to store in localStorage
 */
export const getUserTypeStorageValue = userType => {
  if (userType === USER_TYPES.PATIENT) {
    return USER_TYPES.PATIENT;
  }
  if (userType === USER_TYPES.DASHBOARD) {
    return USER_TYPES.DASHBOARD;
  }
  if (userType === USER_TYPES.CLINIC_OWNER) {
    return USER_TYPES.CLINIC_OWNER;
  }
  if (userType === USER_TYPES.MEDICAL_DIRECTOR) {
    return USER_TYPES.MEDICAL_DIRECTOR;
  }
  if (userType === USER_TYPES.RECEPTIONIST) {
    return USER_TYPES.RECEPTIONIST;
  }
  return USER_TYPES.THERAPIST;
};

/**
 * Set user type in both Zustand store and localStorage
 * @param {Function} setUserType - Zustand setUserType function
 * @param {USER_TYPES} userType - The user type to set
 */
export const setUserTypeEverywhere = (setUserType, userType) => {
  setUserType(userType);
  localStorage.setItem('user-type', getUserTypeStorageValue(userType));
};
